// ./src/components/features/TireSets/TireSetList/TireSetList.tsx
//
// List component for the Tire Sets page.
// Renders tire set records as grouped cards (Active / Stored / Retired)
// with Framer Motion entrance animations.
//
// Context support (matches LogbookList / RevenuesList pattern):
// - When rendered inside a <TireSetsProvider>, data props (tireSets, loading,
//   loadingMore, hasMore, onLoadMore, deletingTireSetId) are read from context
//   automatically. Props can still override context values if provided.
// - When rendered outside a provider, all data must be passed via props.
//
// Design decisions:
// - Grouped-by-status sections (Active, Stored, Retired) for domain clarity
// - Swipeable cards on mobile, inline action buttons on desktop (no footer)
// - Specialized card content (items, tread depth, warranties, expense timeline)
//   — too complex for generic RecordCard

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Skeleton,
  Divider,
  ThemeIcon,
  Tooltip,
  Badge,
  Progress,
  UnstyledButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";

import {
  TireSet,
  TireSetItem,
  TIRE_SET_STATUS,
  TIRE_TYPE,
  TIRE_POSITION,
  TIRE_CONDITION,
  TIRE_EXPENSE_KIND,
  TIRE_WARNING_DEFAULTS,
  getTireSetSummary,
  parseDotCode,
  getWarningSeverity,
  Expense,
} from "@/utils/api";
import { TIRE_TYPE_MAP } from "@/config/tireOptions";
import { useAppEnvironment, useOptionalTireSetsContext } from "@/contexts";
import {
  AppButton,
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  TireWarningBadge,
  TireTypeBadge,
  TireSetStatusBadge,
  LoadMoreButton,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

// =============================================================================
// Props
// =============================================================================

export interface TireSetListProps {
  /**
   * Array of tire set records to display.
   * Optional when inside a TireSetsProvider — reads from context.
   */
  tireSets?: TireSet[];

  /**
   * Loading state for initial load.
   * Optional when inside a TireSetsProvider — reads from context.
   */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — not yet in context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEditTireSet?: (tireSet: TireSet) => void;

  /** Callback when delete action is clicked */
  onDeleteTireSet?: (tireSet: TireSet) => void;

  /** Callback when swap action is clicked */
  onSwapTireSet?: (tireSet: TireSet) => void;

  /** ID of tire set currently being deleted. Optional — reads from context. */
  deletingTireSetId?: string | null;

  /** ID of recently edited/added tire set to highlight */
  highlightedTireSetId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddTireSet?: () => void;
}

// =============================================================================
// Constants & Config
// =============================================================================

const POSITION_CONFIG: Record<string, { color: string; labelKey: string }> = {
  [TIRE_POSITION.ALL]: { color: "gray", labelKey: "tireSets:position.all" },
  [TIRE_POSITION.FRONT]: { color: "blue", labelKey: "tireSets:position.front" },
  [TIRE_POSITION.REAR]: { color: "orange", labelKey: "tireSets:position.rear" },
};

const CONDITION_CONFIG: Record<string, { color: string; labelKey: string }> = {
  [TIRE_CONDITION.NEW]: { color: "teal", labelKey: "tireSets:condition.new" },
  [TIRE_CONDITION.USED]: { color: "yellow", labelKey: "tireSets:condition.used" },
  [TIRE_CONDITION.CAME_WITH_VEHICLE]: { color: "gray", labelKey: "tireSets:condition.cameWithVehicle" },
};

const EXPENSE_KIND_LABELS: Record<number, string> = {
  [TIRE_EXPENSE_KIND.TIRE_REPAIR_REPLACEMENT]: "tireSets:expenseKind.purchase",
  [TIRE_EXPENSE_KIND.SEASONAL_TIRE_SERVICE]: "tireSets:expenseKind.swap",
  [TIRE_EXPENSE_KIND.TIRE_BALANCE]: "tireSets:expenseKind.balance",
  [TIRE_EXPENSE_KIND.TIRE_ROTATION]: "tireSets:expenseKind.rotation",
};

const EXPENSE_COLLAPSED_COUNT = 2;

// =============================================================================
// Helpers
// =============================================================================

function getProgressColor(
  ratio: number,
  warningAt = 0.7,
  criticalAt = 1.0
): string {
  if (ratio >= criticalAt) return "red";
  if (ratio >= warningAt) return "yellow";
  return "teal";
}

function getTreadColor(depth: number, limit: number): string {
  if (depth <= limit) return "red";
  if (depth <= limit * 1.3) return "yellow";
  return "teal";
}

// =============================================================================
// Animation Variants (item rows — card-level uses shared entrance variants)
// =============================================================================

const itemRowVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      delay: 0.15 + i * 0.06,
      ease: [0.25, 0.1, 0.25, 1] as const,
    },
  }),
};

// =============================================================================
// Warranty Progress Bar
// =============================================================================

interface WarrantyProgressProps {
  value: number;
  max: number;
  unit: string;
}

const WarrantyProgressComponent: React.FC<WarrantyProgressProps> = ({
  value,
  max,
  unit,
}) => {
  const { t } = useTranslation(["tireSets"]);

  if (max <= 0) return null;

  const pct = Math.min((value / max) * 100, 100);
  const ratio = value / max;
  const color = getProgressColor(ratio);

  return (
    <Box mt={4}>
      <Group justify="space-between" gap={8} mb={3}>
        <Text size="xs" c="dimmed">
          {t("tireSets:itemFields.warranty")}
        </Text>
        <Text size="xs" fw={600} c={color}>
          {value.toLocaleString()} / {max.toLocaleString()} {unit}
        </Text>
      </Group>
      <Progress value={pct} color={color} size={4} radius="xl" />
    </Box>
  );
};

const WarrantyProgress = React.memo(WarrantyProgressComponent);

// =============================================================================
// Tread Depth Indicator
// =============================================================================

interface TreadDepthBarProps {
  currentDepth: number | null | undefined;
  initialDepth: number | null | undefined;
  treadLimit: number;
  maxDepth?: number;
}

const TreadDepthBar: React.FC<TreadDepthBarProps> = ({
  currentDepth,
  initialDepth,
  treadLimit,
  maxDepth = 10,
}) => {
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslation(["tireSets"]);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const depth = currentDepth ?? initialDepth;
  if (depth == null) return null;

  const isCurrent = currentDepth != null;
  const percent = Math.min(100, Math.max(0, (depth / maxDepth) * 100));
  const color = getTreadColor(depth, treadLimit);

  return (
    <Group gap={6} wrap="nowrap">
      <Box
        style={{
          width: 36,
          height: 5,
          borderRadius: 3,
          backgroundColor: "var(--mantine-color-gray-2)",
          overflow: "hidden",
        }}
      >
        <motion.div
          initial={{ width: "0%" }}
          animate={{ width: mounted ? `${percent}%` : "0%" }}
          transition={{
            duration: 0.6,
            ease: [0.34, 1.56, 0.64, 1],
            delay: 0.3,
          }}
          style={{
            height: "100%",
            borderRadius: 3,
            backgroundColor: `var(--mantine-color-${color}-5)`,
          }}
        />
      </Box>
      <Text size="sm" fw={600} c={color}>
        {depth} {t("tireSets:threshold.treadUnit")}
      </Text>
      {!isCurrent && (
        <Text size="xs" c="dimmed">
          ({t("tireSets:item.initialTread")})
        </Text>
      )}
    </Group>
  );
};

// =============================================================================
// DOT Age Badge
// =============================================================================

interface DotAgeBadgeProps {
  dotCode: string | null | undefined;
}

const DotAgeBadge: React.FC<DotAgeBadgeProps> = ({ dotCode }) => {
  const { t } = useTranslation(["tireSets"]);
  const parsed = parseDotCode(dotCode);

  if (!parsed) return null;

  const label = `${t("tireSets:item.manufactured")}: ${parsed.week}/${parsed.year}`;

  return (
    <Tooltip label={label} withArrow position="top">
      <Badge
        size="xs"
        variant="light"
        color="gray"
        style={{ cursor: "help", fontFamily: "monospace" }}
      >
        DOT {parsed.week}/{parsed.year}
      </Badge>
    </Tooltip>
  );
};

// =============================================================================
// Mileage Breakdown
// =============================================================================

interface MileageBreakdownProps {
  item: TireSetItem;
  mileageUnit: string;
  warrantyDistance: number;
}

const MileageBreakdownComponent: React.FC<MileageBreakdownProps> = ({
  item,
  mileageUnit,
  warrantyDistance,
}) => {
  const { t } = useTranslation(["tireSets"]);

  const hasPrevious =
    item.mileageAccumulatedPreviousDisplay != null &&
    item.mileageAccumulatedPreviousDisplay > 0;
  const hasSinceInstall =
    item.mileageSinceInstallDisplay != null &&
    item.mileageSinceInstallDisplay > 0;
  const hasTotal =
    item.mileageTotalDisplay != null && item.mileageTotalDisplay > 0;

  if (!hasTotal && !hasSinceInstall && !hasPrevious) return null;

  const totalDisplay = item.mileageTotalDisplay ?? 0;
  const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString();

  return (
    <Box pt={4}>
      <Group gap="lg" wrap="wrap" style={{ rowGap: 2 }}>
        {hasPrevious && (
          <Text size="sm" c="dimmed">
            {t("tireSets:itemFields.mileageAccumulatedPrevious")}:{" "}
            <Text span fw={500} c="dark">
              {fmt(item.mileageAccumulatedPreviousDisplay)} {mileageUnit}
            </Text>
          </Text>
        )}
        {hasSinceInstall && (
          <Text size="sm" c="dimmed">
            {t("tireSets:itemFields.mileageSinceInstall")}:{" "}
            <Text span fw={500} c="dark">
              {fmt(item.mileageSinceInstallDisplay)} {mileageUnit}
            </Text>
          </Text>
        )}
        {hasTotal && (
          <Text size="sm" c="dimmed">
            {t("tireSets:itemFields.mileageTotal")}:{" "}
            <Text span fw={600} c="dark">
              {fmt(totalDisplay)} {mileageUnit}
            </Text>
          </Text>
        )}
      </Group>

      {warrantyDistance > 0 && hasTotal && (
        <WarrantyProgress
          value={totalDisplay}
          max={warrantyDistance}
          unit={mileageUnit}
        />
      )}
    </Box>
  );
};

const MileageBreakdown = React.memo(MileageBreakdownComponent);

// =============================================================================
// Tire Set Item Row
// =============================================================================

interface TireSetItemRowProps {
  item: TireSetItem;
  index: number;
  skipAnimation?: boolean;
  mileageUnit: string;
  warrantyDistance: number;
  treadLimit: number;
}

const TireSetItemRowComponent: React.FC<TireSetItemRowProps> = ({
  item,
  index,
  skipAnimation = false,
  mileageUnit,
  warrantyDistance,
  treadLimit,
}) => {
  const { t } = useTranslation(["tireSets"]);
  const posConfig =
    POSITION_CONFIG[item.position || TIRE_POSITION.ALL] ||
    POSITION_CONFIG[TIRE_POSITION.ALL];
  const condConfig =
    CONDITION_CONFIG[item.tireCondition || TIRE_CONDITION.NEW] ||
    CONDITION_CONFIG[TIRE_CONDITION.NEW];
  const showPosition = item.position && item.position !== TIRE_POSITION.ALL;

  return (
    <motion.div
      custom={index}
      variants={itemRowVariants}
      initial={skipAnimation ? false : "hidden"}
      animate="visible"
    >
      <Stack
        gap={2}
        py={8}
        px={10}
        style={{
          borderRadius: "var(--mantine-radius-sm)",
          backgroundColor: "var(--mantine-color-gray-0)",
        }}
      >
        {/* Line 1: Brand/Model + Size + Badges */}
        <Group gap="xs" wrap="wrap">
          <Text size="sm" fw={700} c="dimmed" style={{ flexShrink: 0 }}>
            {item.quantity}×
          </Text>
          <Text size="md" fw={600} lineClamp={1}>
            {[item.brand, item.model].filter(Boolean).join(" ") ||
              t("tireSets:item.unknownTire")}
          </Text>
          {item.tireSize && (
            <Text size="sm" c="dimmed" ff="monospace">
              {item.tireSize}
            </Text>
          )}
          {showPosition && (
            <Badge size="xs" variant="dot" color={posConfig.color}>
              {t(posConfig.labelKey)}
            </Badge>
          )}
          <Badge size="xs" variant="light" color={condConfig.color}>
            {t(condConfig.labelKey)}
          </Badge>
        </Group>

        {/* Line 2: Odometer, Tread, DOT, Warnings */}
        <Group gap="md" wrap="wrap" style={{ rowGap: 4 }}>
          {item.odometerAtInstall != null && (
            <Text size="sm" c="dimmed">
              {t("tireSets:itemFields.odometerAtInstall")}:{" "}
              <Text span fw={500} c="dark">
                {item.odometerAtInstall.toLocaleString()}
              </Text>
            </Text>
          )}
          {(item.treadDepthCurrent != null ||
            item.treadDepthInitial != null) && (
              <Group gap={4} wrap="nowrap">
                <Text size="sm" c="dimmed">
                  {t("tireSets:itemFields.treadDepth")}:
                </Text>
                <TreadDepthBar
                  currentDepth={item.treadDepthCurrent}
                  initialDepth={item.treadDepthInitial}
                  treadLimit={treadLimit}
                />
              </Group>
            )}
          <DotAgeBadge dotCode={item.dotCode} />
          <TireWarningBadge
            warningFlags={item.warningFlags}
            variant="badge"
            scope="item"
          />
        </Group>

        {/* Line 3: Mileage breakdown + Warranty progress */}
        <MileageBreakdown
          item={item}
          mileageUnit={mileageUnit}
          warrantyDistance={warrantyDistance}
        />
      </Stack>
    </motion.div>
  );
};

const TireSetItemRow = React.memo(TireSetItemRowComponent);

// =============================================================================
// Linked Expenses Mini-Timeline (collapsible)
// =============================================================================

interface ExpenseTimelineProps {
  expenses: Expense[] | undefined;
}

const ExpenseTimelineComponent: React.FC<ExpenseTimelineProps> = ({
  expenses,
}) => {
  const { t } = useTranslation(["tireSets"]);
  const [expanded, setExpanded] = useState(false);

  const sortedExpenses = useMemo(() => {
    if (!expenses || expenses.length === 0) return [];
    return [...expenses].sort((a, b) => {
      const da = a.whenDone ? new Date(a.whenDone).getTime() : 0;
      const db = b.whenDone ? new Date(b.whenDone).getTime() : 0;
      return db - da;
    });
  }, [expenses]);

  if (sortedExpenses.length === 0) return null;

  const visible = expanded
    ? sortedExpenses
    : sortedExpenses.slice(0, EXPENSE_COLLAPSED_COUNT);
  const hasMore = sortedExpenses.length > EXPENSE_COLLAPSED_COUNT;
  const hiddenCount = sortedExpenses.length - EXPENSE_COLLAPSED_COUNT;

  const totalSpent = useMemo(
    () => sortedExpenses.reduce((s, e) => s + (e.totalPrice ?? 0), 0),
    [sortedExpenses]
  );
  const currency = sortedExpenses[0]?.paidInCurrency || "";

  return (
    <Stack gap={4}>
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed" fw={500} tt="uppercase" lts={0.5}>
          {t("tireSets:list.recentExpenses")}
        </Text>
        {totalSpent > 0 && (
          <Text size="sm" fw={600}>
            {t("tireSets:list.expenseTotal")}: {currency}{" "}
            {totalSpent.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        )}
      </Group>

      {visible.map((exp) => {
        const kindLabelKey =
          exp.kindId != null ? EXPENSE_KIND_LABELS[exp.kindId] : undefined;
        return (
          <Group key={exp.id} gap={8} wrap="nowrap">
            <Box
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                backgroundColor:
                  (exp.totalPrice ?? 0) > 0
                    ? "var(--mantine-color-blue-4)"
                    : "var(--mantine-color-gray-4)",
                flexShrink: 0,
              }}
            />
            <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
              {exp.whenDone
                ? dayjs(exp.whenDone).format("MMM DD, YYYY")
                : "—"}
            </Text>
            {kindLabelKey && (
              <Text size="sm" c="dimmed">
                {t(kindLabelKey)}
              </Text>
            )}
            {exp.totalPrice != null && (
              <Text
                size="sm"
                fw={600}
                c={(exp.totalPrice ?? 0) > 0 ? undefined : "dimmed"}
              >
                {exp.paidInCurrency || ""}{" "}
                {exp.totalPrice.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Text>
            )}
            {exp.shortNote && (
              <Text size="sm" c="dimmed" lineClamp={1} style={{ flex: 1 }}>
                {exp.shortNote}
              </Text>
            )}
          </Group>
        );
      })}

      {hasMore && (
        <UnstyledButton
          onClick={() => setExpanded((v) => !v)}
          style={{ alignSelf: "flex-start" }}
        >
          <Text size="sm" c="blue" fw={500}>
            {expanded
              ? t("common:buttons.showLess")
              : `+${hiddenCount} ${t("tireSets:list.moreExpenses")}`}
          </Text>
        </UnstyledButton>
      )}
    </Stack>
  );
};

const ExpenseTimeline = React.memo(ExpenseTimelineComponent);

// =============================================================================
// Loading Skeletons
// =============================================================================

const TireSetCardSkeleton: React.FC = () => (
  <Paper p="lg" withBorder radius="md">
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={6}>
          <Skeleton height={24} width={200} />
          <Skeleton height={16} width={280} />
        </Stack>
      </Group>
      <Group gap={8}>
        <Skeleton height={22} width={80} radius="xl" />
        <Skeleton height={22} width={70} radius="xl" />
      </Group>
      <Skeleton height={1} width="100%" />
      <Stack gap={6}>
        <Skeleton height={80} width="100%" radius="sm" />
        <Skeleton height={80} width="100%" radius="sm" />
      </Stack>
      <Skeleton height={1} width="100%" />
      <Group gap="lg">
        <Skeleton height={14} width={120} />
        <Skeleton height={14} width={100} />
      </Group>
    </Stack>
  </Paper>
);

const LoadingSkeleton: React.FC = () => (
  <Stack gap="md">
    {Array.from({ length: 3 }).map((_, i) => (
      <TireSetCardSkeleton key={i} />
    ))}
  </Stack>
);

// =============================================================================
// Sub-Components
// =============================================================================

const DetailItem: React.FC<{ icon: string; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <Group gap={6} wrap="nowrap">
    <AppIcon iconName={icon} size={16} color="gray" style={{ flexShrink: 0 }} />
    <Text size="sm" c="dimmed" lineClamp={1}>
      {children}
    </Text>
  </Group>
);

// =============================================================================
// Tire Set Card
// =============================================================================

interface TireSetCardProps {
  tireSet: TireSet;
  onEdit?: (tireSet: TireSet) => void;
  onDelete?: (tireSet: TireSet) => void;
  onSwap?: (tireSet: TireSet) => void;
  highlighted: boolean;
  isDeleting: boolean;
  skipAnimation?: boolean;
  isMobile: boolean;
  // Swipe management
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TireSetCardComponent: React.FC<TireSetCardProps> = ({
  tireSet,
  onEdit,
  onDelete,
  onSwap,
  highlighted,
  isDeleting,
  skipAnimation = false,
  isMobile,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["tireSets", "common"]);

  const typeOption =
    TIRE_TYPE_MAP[tireSet.tireType || TIRE_TYPE.ALL_SEASON] ||
    TIRE_TYPE_MAP[TIRE_TYPE.ALL_SEASON];
  const isRetired = tireSet.status === TIRE_SET_STATUS.RETIRED;
  const isStored = tireSet.status === TIRE_SET_STATUS.STORED;
  const isActive = tireSet.status === TIRE_SET_STATUS.ACTIVE;

  const mileageUnit = tireSet.car?.mileageIn || "km";
  const warrantyDistance = useMemo(() => tireSet.mileageWarranty ?? 0, [tireSet.mileageWarranty]);
  const treadLimit = tireSet.treadLimitMm ?? TIRE_WARNING_DEFAULTS.TREAD_LIMIT_MM;

  const summary = useMemo(() => getTireSetSummary(tireSet.items), [tireSet.items]);
  const setWarningSeverity = getWarningSeverity(tireSet.warningFlags);

  // Detail items (bottom section)
  const detailItems = useMemo(() => {
    const items: { icon: string; text: string }[] = [];

    if (tireSet.storageLocation && isStored) {
      items.push({
        icon: "MapPin",
        text: `${t("tireSets:list.storedAt")}: ${tireSet.storageLocation}`,
      });
    }

    if (tireSet.car?.label) {
      items.push({ icon: "Car", text: tireSet.car.label });
    }

    if (tireSet.installedAt && isActive) {
      items.push({
        icon: "CalendarCheck",
        text: `${t("tireSets:field.installedAt")}: ${dayjs(tireSet.installedAt).format("MMM DD, YYYY")}`,
      });
    } else if (tireSet.storedAt && isStored) {
      items.push({
        icon: "CalendarWeek",
        text: `${t("tireSets:field.storedAt")}: ${dayjs(tireSet.storedAt).format("MMM DD, YYYY")}`,
      });
    } else if (tireSet.createdAt) {
      items.push({
        icon: "CalendarWeek",
        text: `${t("tireSets:list.added")}: ${dayjs(tireSet.createdAt).format("MMM DD, YYYY")}`,
      });
    }

    return items;
  }, [tireSet, isStored, isActive, t]);

  // Swipe actions (mobile)
  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onSwap && isStored) {
      actions.push({
        key: "swap",
        icon: "ArrowsExchange",
        label: t("tireSets:actions.install"),
        color: "teal",
        onClick: () => onSwap(tireSet),
        disabled: isDeleting,
      });
    }

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(tireSet),
        disabled: isDeleting,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(tireSet),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onSwap, onEdit, onDelete, tireSet, isStored, isDeleting, t]);

  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => onSwipeStateChange?.(isOpen),
    [onSwipeStateChange]
  );

  // Card border color based on warning severity
  const borderLeftColor = useMemo(() => {
    if (setWarningSeverity === "critical") return "var(--mantine-color-red-5)";
    if (setWarningSeverity === "warning") return "var(--mantine-color-yellow-5)";
    return `var(--mantine-color-${typeOption.color}-${isRetired ? "2" : "5"})`;
  }, [setWarningSeverity, typeOption.color, isRetired]);

  // ── Card content ──
  const cardContent = (
    <Paper
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 200ms ease",
        borderLeft: `3px solid ${borderLeftColor}`,
        overflow: "hidden",
        ...(highlighted
          ? {
            borderColor: "var(--mantine-color-teal-5)",
            boxShadow:
              "0 0 0 1px var(--mantine-color-teal-2), 0 4px 16px var(--mantine-color-teal-1)",
          }
          : {}),
      }}
    >
      <Stack gap="md" p="lg">
        {/* Header: Name + Summary + Warning */}
        <Stack gap={4}>
          <Group gap={8} wrap="nowrap">
            <Text size="xl" fw={700} lineClamp={1}>
              {tireSet.name || t("tireSets:list.unnamedSet")}
            </Text>
            <TireWarningBadge
              warningFlags={tireSet.warningFlags}
              variant="icon"
              scope="set"
            />
          </Group>
          <Text size="sm" c="dimmed" lineClamp={1}>
            {summary}
          </Text>
        </Stack>

        {/* Type + Status badges + tire count */}
        <Group gap="sm" wrap="wrap">
          <TireTypeBadge value={tireSet.tireType} />
          <TireSetStatusBadge value={tireSet.status} />
          {isActive && (
            <Badge size="sm" variant="filled" color="teal" radius="sm">
              {t("tireSets:list.onVehicle")}
            </Badge>
          )}
          <Text size="sm" c="dimmed">
            {tireSet.quantity} {t("tireSets:list.tiresCount")}
          </Text>
        </Group>

        <Divider />

        {/* Tire items */}
        {tireSet.items && tireSet.items.length > 0 && (
          <Stack gap={6}>
            {tireSet.items.map((item, index) => (
              <TireSetItemRow
                key={item.id || index}
                item={item}
                index={index}
                skipAnimation={skipAnimation}
                mileageUnit={mileageUnit}
                warrantyDistance={warrantyDistance}
                treadLimit={treadLimit}
              />
            ))}
          </Stack>
        )}

        {/* Linked expenses timeline */}
        {tireSet.expenses && tireSet.expenses.length > 0 && (
          <>
            <Divider variant="dashed" />
            <ExpenseTimeline expenses={tireSet.expenses} />
          </>
        )}

        {/* Bottom details */}
        {detailItems.length > 0 && (
          <>
            <Divider variant="dashed" />
            <Group gap="md" wrap="wrap" style={{ rowGap: 6 }}>
              {detailItems.map((item, idx) => (
                <DetailItem key={idx} icon={item.icon}>
                  {item.text}
                </DetailItem>
              ))}
            </Group>
          </>
        )}

        {/* Notes */}
        {tireSet.notes && (
          <Text size="sm" c="dimmed" fs="italic" lineClamp={2}>
            {tireSet.notes}
          </Text>
        )}
      </Stack>

      {/* Desktop inline action buttons (no footer bar) */}
      {!isMobile && (onEdit || onDelete || (onSwap && isStored)) && (
        <Group
          justify="flex-end"
          gap={8}
          px="md"
          py="xs"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-1)",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          {onSwap && isStored && (
            <AppButton
              iconName="ArrowsExchange"
              variant="secondary"
              size="sm"
              onClick={() => onSwap(tireSet)}
              disabled={isDeleting}
            >
              {t("tireSets:actions.install")}
            </AppButton>
          )}
          {onEdit && (
            <AppButton
              iconName="Edit"
              variant="secondary"
              size="sm"
              onClick={() => onEdit(tireSet)}
              disabled={isDeleting}
            >
              {t("common:buttons.edit")}
            </AppButton>
          )}
          {onDelete && (
            <AppButton
              iconName="Trash"
              variant="secondary"
              size="sm"
              color="red"
              onClick={() => onDelete(tireSet)}
              loading={isDeleting}
              disabled={isDeleting}
            >
              {t("common:buttons.delete")}
            </AppButton>
          )}
        </Group>
      )}
    </Paper>
  );

  // Wrap in SwipeableCard on mobile
  if (isMobile && leftSwipeActions.length > 0) {
    return (
      <SwipeableCard
        leftSwipeActions={leftSwipeActions}
        forceClose={forceClose}
        onForceCloseComplete={onForceCloseComplete}
        onSwipeStateChange={handleSwipeStateChange}
        disabled={isDeleting}
        borderRadius="var(--mantine-radius-md)"
      >
        {cardContent}
      </SwipeableCard>
    );
  }

  return cardContent;
};

const TireSetCard = React.memo(TireSetCardComponent);

// =============================================================================
// Main Component
// =============================================================================

const TireSetListComponent: React.FC<TireSetListProps> = ({
  tireSets: tireSetsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditTireSet,
  onDeleteTireSet,
  onSwapTireSet,
  deletingTireSetId: deletingTireSetIdProp,
  highlightedTireSetId,
  onAddTireSet,
}) => {
  const { t } = useTranslation(["tireSets", "common"]);
  const { isMobile } = useAppEnvironment();

  // ── Context vs Props resolution ──
  const tireSetsContext = useOptionalTireSetsContext();

  const tireSets = tireSetsProp ?? tireSetsContext?.tireSets ?? [];
  const loading = loadingProp ?? tireSetsContext?.isLoading ?? false;
  const loadingMore = loadingMoreProp ?? false;
  const hasMore = hasMoreProp ?? false;
  const onLoadMore = onLoadMoreProp;
  const deletingTireSetId = deletingTireSetIdProp ?? null;

  // Swipeable card management
  const { openCard, shouldForceClose, handleForceCloseComplete } =
    useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: tireSets.length });

  const handleSwipeStateChange = useCallback(
    (tireSetId: string, isOpen: boolean) => {
      if (isOpen) openCard(tireSetId);
    },
    [openCard]
  );

  // Group tire sets by status
  const groupedSets = useMemo(() => {
    const active = tireSets.filter((ts) => ts.status === TIRE_SET_STATUS.ACTIVE);
    const stored = tireSets.filter((ts) => ts.status === TIRE_SET_STATUS.STORED);
    const retired = tireSets.filter((ts) => ts.status === TIRE_SET_STATUS.RETIRED);
    return { active, stored, retired };
  }, [tireSets]);

  const hasGroupedView =
    groupedSets.active.length > 0 || groupedSets.stored.length > 0;

  // ── Render helpers ──

  const renderCard = useCallback(
    (tireSet: TireSet) => (
      <motion.div
        key={tireSet.id}
        variants={hasAnimated ? undefined : entranceCardVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        exit="exit"
        layout
      >
        <TireSetCard
          tireSet={tireSet}
          onEdit={onEditTireSet}
          onDelete={onDeleteTireSet}
          onSwap={onSwapTireSet}
          highlighted={tireSet.id === highlightedTireSetId}
          isDeleting={deletingTireSetId === tireSet.id}
          skipAnimation={hasAnimated}
          isMobile={!!isMobile}
          forceClose={shouldForceClose(tireSet.id || "")}
          onForceCloseComplete={() =>
            handleForceCloseComplete(tireSet.id || "")
          }
          onSwipeStateChange={(isOpen) =>
            handleSwipeStateChange(tireSet.id || "", isOpen)
          }
        />
      </motion.div>
    ),
    [
      onEditTireSet,
      onDeleteTireSet,
      onSwapTireSet,
      highlightedTireSetId,
      deletingTireSetId,
      hasAnimated,
      isMobile,
      shouldForceClose,
      handleForceCloseComplete,
      handleSwipeStateChange,
    ]
  );

  const renderSection = useCallback(
    (
      label: string,
      icon: string,
      color: string,
      sets: TireSet[]
    ) => {
      if (sets.length === 0) return null;
      return (
        <Stack gap="sm">
          <Group gap={8} wrap="nowrap">
            <ThemeIcon variant="light" color={color} size="xs" radius="xl">
              <AppIcon iconName={icon} size={12} />
            </ThemeIcon>
            <Text size="sm" fw={600} c={color} tt="uppercase" lts={0.5}>
              {label}
            </Text>
            <Badge size="xs" variant="light" color={color} circle>
              {sets.length}
            </Badge>
          </Group>
          <AnimatePresence mode="sync">
            {sets.map((ts) => renderCard(ts))}
          </AnimatePresence>
        </Stack>
      );
    },
    [renderCard]
  );

  // ── Loading state ──

  if (loading && tireSets.length === 0) {
    return <LoadingSkeleton />;
  }

  // ── Empty state ──

  if (!loading && tireSets.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("tireSets:emptyState.title")}
          description={t("tireSets:emptyState.description")}
          iconName="Circle"
          actionLabel={t("tireSets:emptyState.actionLabel")}
          onAction={onAddTireSet}
          size="md"
        />
      </Paper>
    );
  }

  // ── Main render ──

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-lg)",
        }}
      >
        {hasGroupedView ? (
          <>
            {renderSection(
              t("tireSets:list.sections.active"),
              "CircleCheck",
              "teal",
              groupedSets.active
            )}
            {renderSection(
              t("tireSets:list.sections.stored"),
              "Package",
              "blue",
              groupedSets.stored
            )}
            {groupedSets.retired.length > 0 &&
              renderSection(
                t("tireSets:list.sections.retired"),
                "CircleX",
                "gray",
                groupedSets.retired
              )}
          </>
        ) : (
          <AnimatePresence mode="sync">
            {tireSets.map((ts) => renderCard(ts))}
          </AnimatePresence>
        )}
      </motion.div>

      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const TireSetList = React.memo(TireSetListComponent);